import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaSave, FaTimes } from "react-icons/fa";
import Select from "react-select";
import SideNav from "../common/sidenav";
import TopNav from "../common/topnav";
import Loading from "../loading/loading";
import "../common/style.css";
import "./manageBatch.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getJobrole,
  getState,
  getCandidateByProject,
  createBatch,
  selectTPName,
  fetchBatchById,
} from "../http/api";
import CandidateTable from "./candidateTable";

export default function ManageBatchForm({ mode }) {
  const { id, projectId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(
    localStorage.getItem("sb|sidebar-toggle") === "true"
  );

  const isAddMode = mode === "add";

  // Form state
  const [localFormData, setLocalFormData] = useState({
    id: isAddMode ? null : id,
    batchId: "",
    state: "",
    location: "",
    jobRole: "",
    tpName: "",
    trainerName : "",
    startDate: "",
    endDate: "",
    status: "Not Started",
    projectId: projectId || null,
  });

  const [candidates, setCandidates] = useState([]);
  const [errors, setErrors] = useState({});
  const [candidateErrors, setCandidateErrors] = useState({});
  const [selectedCandidateIds, setSelectedCandidateIds] = useState(new Set());

  // Data for dropdowns
  const [states, setStates] = useState([]);
  const [jobRoles, setJobRoles] = useState([]);
  const [trainingPartners, setTrainingPartners] = useState([]);
  const statusOptions = [
    { value: "Not Started", label: "Not Started" },
    { value: "Ongoing", label: "Ongoing" },
    { value: "Cancelled", label: "Cancelled" },
    { value: "Completed", label: "Completed" },
  ];

  useEffect(() => {
    localStorage.setItem("sb|sidebar-toggle", isSidebarOpen);
  }, [isSidebarOpen]);

  // Load candidate options for AsyncSelect
  // const loadCandidateOptions = async (inputValue) => {
  //   try {
  //     if (!projectId) {
  //       console.error("Project ID is missing");
  //       return [];
  //     }

  //     const response = await getCandidateByProject(projectId, 0, 50, inputValue);

      
  //     const candidates = Array.isArray(response)
  //       ? response
  //       : Array.isArray(response?.data)
  //       ? response.data
  //       : [];

  //     return candidates
  //       .filter((candidate) => {
  //         if (!candidate || selectedCandidateIds.has(candidate.id))
  //           return false;

  //         const searchFields = [
  //           candidate.candidateName?.toString().toLowerCase() || "",
  //           candidate.mobile?.toString() || "",
  //           candidate.aadharNumber?.toString() || "",
  //           candidate.guardianName?.toString().toLowerCase() || "",
  //         ];

  //         const searchTerm = inputValue.toLowerCase();
  //         return searchFields.some((field) => field.includes(searchTerm));
  //       })
  //       .map((candidate) => ({
  //         value: candidate.id || candidate.candidateId,
  //         label: `${candidate.candidateName || "Unknown"} (${
  //           candidate.mobile || "No mobile"
  //         })`,
  //         data: {
  //           candidateName: candidate.candidateName || "",
  //           mobile: candidate.mobile || "",
  //           aadharNumber: candidate.aadharNumber || "",
  //           guardianName: candidate.guardianName || "",
  //         },
  //       }));
  //   } catch (error) {
  //     console.error("Error loading candidates:", error);
  //     return [];
  //   }
  // };

  const loadCandidateOptions = async (inputValue = "") => {
  try {
    if (!projectId) {
      console.error("Project ID missing");
      return [];
    }

    const response = await getCandidateByProject(projectId, 0, 50, inputValue);
    console.log("📡 getCandidateByProject Response:", response);

    if (!response || response.status === 204 || !response.data) {
      console.warn("⚠️ No candidates returned from backend");
      return [];
    }

    // ✅ Support for both array and paginated API responses
    const data = Array.isArray(response.data)
      ? response.data
      : response.data.content || response.data || [];

    if (!Array.isArray(data)) {
      console.warn("⚠️ Unexpected candidate data format:", data);
      return [];
    }

    // ✅ Filter already selected candidates + apply search term
    const searchTerm = inputValue?.toLowerCase() || "";
    const filtered = data.filter((candidate) => {
      if (!candidate || selectedCandidateIds.has(candidate.id)) return false;
      return (
        candidate.candidateName?.toLowerCase().includes(searchTerm) ||
        candidate.mobile?.toString().includes(searchTerm) ||
        candidate.aadharNumber?.toString().includes(searchTerm) ||
        candidate.guardianName?.toLowerCase().includes(searchTerm)
      );
    });

    // ✅ Return mapped options for AsyncSelect
    return filtered.map((candidate) => ({
      value: candidate.id,
      label: `${candidate.candidateName || "Unknown"} (${candidate.mobile || "N/A"})`,
      data: candidate,
    }));
  } catch (error) {
    console.error("❌ Error loading candidates:", error);
    return [];
  }
};


  // Fetch initial data based on mode
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);

        // Fetch States
        const statesResponse = await getState();
        const formattedStates = statesResponse.data.map((state) => ({
          value: state.name,
          label: state.name,
        }));
        setStates(formattedStates);

        // Fetch Job Roles
        const jobRolesResponse = await getJobrole();
        const formattedJobRoles = jobRolesResponse.data.map((role) => ({
          value: role.name,
          label: role.name,
        }));
        setJobRoles(formattedJobRoles);

        // Fetch Training Partners
        const tpResponse = await selectTPName();
        const formattedTPs = tpResponse.data.map((tp) => ({
          value: tp.id,
          label: `${tp.tpname} (${tp.name})`,
        }));
        setTrainingPartners(formattedTPs);

        if (mode === "edit" && id) {
          const batchResponse = await fetchBatchById(id);
          const batchData = batchResponse.data;
          setLocalFormData({
            id: batchData.id,
            batchId: batchData.batchId,
            state: batchData.state,
            location: batchData.location,
            jobRole: batchData.jobRole,
            tpName: batchData.trainingPartner?.id || "",
            trainerName : batchData.trainerName,
            startDate: batchData.startDate,
            endDate: batchData.endDate,
            status: batchData.status,
            projectId: batchData.project?.id,
          });
          setCandidates(
            (batchData.candidate || []).map((c) => ({
              candidateName: c.candidateName,
              mobile: c.mobile,
              aadharNumber: c.aadharNumber,
              guardianName: c.guardianName,
              candidateId: c.id,
              selectOption: {
                value: c.id,
                label: `${c.candidateName} (${c.mobile})`,
                data: c,
              },
            }))
          );
          setSelectedCandidateIds(
            new Set((batchData.candidate || []).map((c) => c.id))
          );
        }
      } catch (error) {
        console.error("Error fetching initial data:", error);
        toast.error("Failed to load initial data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [mode, id, projectId]);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLocalFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: null,
      }));
    }
  };

  const handleSelectChange = (name, selectedOption) => {
    setLocalFormData((prevData) => ({
      ...prevData,
      [name]: selectedOption ? selectedOption.value : "",
    }));
    if (errors[name]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: null,
      }));
    }
  };

  const handleCandidateSelect = (index, selectedOption) => {
    const newCandidates = [...candidates];
    const newSelectedIds = new Set(selectedCandidateIds);

    // Remove old ID from the set if it exists
    if (newCandidates[index].candidateId) {
      newSelectedIds.delete(newCandidates[index].candidateId);
    }

    if (selectedOption) {
      const { candidateName, mobile, aadharNumber, guardianName } =
        selectedOption.data;
      newCandidates[index] = {
        ...newCandidates[index],
        candidateName,
        mobile,
        aadharNumber,
        guardianName,
        candidateId: selectedOption.value,
        selectOption: selectedOption,
      };
      newSelectedIds.add(selectedOption.value);
    } else {
      // Handle the case when the user clears the selection
      newCandidates[index] = {
        candidateName: "",
        mobile: "",
        aadharNumber: "",
        guardianName: "",
        candidateId: null,
        selectOption: null,
      };
    }
    setSelectedCandidateIds(newSelectedIds);
    setCandidates(newCandidates);
  };

  const handleAddCandidate = () => {
    setCandidates([
      {
        candidateName: "",
        mobile: "",
        aadharNumber: "",
        guardianName: "",
        candidateId: null,
        selectOption: null,
      },
      ...candidates,
    ]);
  };

  const handleDeleteCandidate = (index) => {
    const newSelectedIds = new Set(selectedCandidateIds);
    if (candidates[index].candidateId) {
      newSelectedIds.delete(candidates[index].candidateId);
    }
    setSelectedCandidateIds(newSelectedIds);
    setCandidates(candidates.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!localFormData.batchId.trim())
      newErrors.batchId = "Batch ID is required.";
    if (!localFormData.state) newErrors.state = "State is required.";
    if (!localFormData.location.trim())
      newErrors.location = "Location is required.";
    if (!localFormData.jobRole) newErrors.jobRole = "Job Role is required.";
    if (!localFormData.tpName)
      newErrors.tpName = "Training Partner is required.";
    if (!localFormData.trainerName.trim()) // Add this validation
      newErrors.trainerName = "Trainer Name is required.";
    if (!localFormData.startDate)
      newErrors.startDate = "Start Date is required.";
    if (!localFormData.endDate) newErrors.endDate = "End Date is required.";
    if (!localFormData.status) newErrors.status = "Status is required.";

    if (localFormData.startDate && localFormData.endDate) {
      const startDate = new Date(localFormData.startDate);
      const endDate = new Date(localFormData.endDate);
      if (endDate < startDate) {
        newErrors.endDate = "End date must be after start date.";
      }
    }
    return newErrors;
  };

  const validateCandidates = () => {
    const newCandidateErrors = {};
    let isValid = true;

    // if (candidates.length === 0) {
    //   toast.error("At least one candidate is required.");
    //   isValid = false;
    // }

    const candidateIds = candidates
      .map((c) => c.candidateId)
      .filter((id) => id !== null);

    if (new Set(candidateIds).size !== candidateIds.length) {
      toast.error("Duplicate candidates are not allowed.");
      isValid = false;
    }

    candidates.forEach((candidate, index) => {
      newCandidateErrors[index] = {};
      if (!candidate.candidateId) {
        newCandidateErrors[index].candidateId = "Please select a candidate.";
        isValid = false;
      }
    });

    setCandidateErrors(newCandidateErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    const formErrors = validateForm();
    const candidatesAreValid = validateCandidates();

    if (Object.keys(formErrors).length > 0 || !candidatesAreValid) {
      setErrors(formErrors);
      if (Object.keys(formErrors).length > 0) {
        toast.error("Please correct the form errors.");
      }
      return;
    }

    setLoading(true);

    const dataToSubmit = {
      batchId: localFormData.batchId,
      state: localFormData.state,
      location: localFormData.location,
      jobRole: localFormData.jobRole,
      startDate: localFormData.startDate,
      endDate: localFormData.endDate,
      status: localFormData.status,
      trainerName: localFormData.trainerName,
      project: {
        id: projectId || localFormData.projectId,
      },
      trainingPartner: {
        id: localFormData.tpName,
      },
      batchCandidates: candidates.map((c) => ({
        candidate: {
          id: c.candidateId,
        },
      })),
    };

    try {
      if (mode === "add") {
        await createBatch(dataToSubmit);
        toast.success("Batch added successfully! Redirecting...");
      } else if (mode === "edit") {
        // const editData = {
        //     id: localFormData.id,
        //     projectId: localFormData.projectId,
        //     ...dataToSubmit,
        //   };
        //   console.log("Updating batch with data:", JSON.stringify(editData, null, 2));
        await createBatch({
          id: localFormData.id,
          projectId: localFormData.projectId,
          ...dataToSubmit,
        });
        toast.success("Batch updated successfully! Redirecting...");
      }
      setTimeout(() => navigate("/managebatch"), 1500);
    } catch (error) {
      console.error(`Error ${isAddMode ? "adding" : "updating"} batch:`, error);
      toast.error(
        `Error ${isAddMode ? "adding" : "updating"} batch. Please try again.`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    toast.info("Action cancelled.");
    navigate(-1);
  };

  // Get the current selected values for the selects
  const getSelectedState = () => {
    return states.find((state) => state.value === localFormData.state) || null;
  };

  const getSelectedJobRole = () => {
    return (
      jobRoles.find((role) => role.value === localFormData.jobRole) || null
    );
  };

  const getSelectedTPName = () => {
    return (
      trainingPartners.find((tp) => tp.value === localFormData.tpName) || null
    );
  };

  const getSelectedStatus = () => {
    return (
      statusOptions.find((status) => status.value === localFormData.status) ||
      null
    );
  };

  return (
    <div className="ManageBatchForm">
      {loading && <Loading />}
      <div
        className={`sb-nav-fixed ${isSidebarOpen ? "sb-sidenav-toggled" : ""}`}
      >
        <TopNav toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} />
        <div id="layoutSidenav">
          <SideNav isSidebarOpen={isSidebarOpen} />
          <div id="layoutSidenav_content">
            <main>
              <div className="container-fluid px-4">
                <h4 className="mt-4">
                  {isAddMode
                    ? "Add New Batch"
                    : `Edit Batch - ${localFormData.batchId}`}
                </h4>
                <div className="card mb-4">
                  <div className="card-header">
                    <i className="fas fa-table me-1"></i>
                    Batch Details Form
                  </div>
                  <div className="card-body shadow-sm border-0">
                    <form className="data-table" style={{ padding: "20px" }}>
                      <div className="row">
                        {/* Batch ID */}
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label htmlFor="batchId" className="form-label">
                              Batch ID <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control form-control-sm ${
                                errors.batchId ? "is-invalid" : ""
                              }`}
                              id="batchId"
                              name="batchId"
                              value={localFormData.batchId || ""}
                              onChange={handleChange}
                              disabled={!isAddMode}
                            />
                            {errors.batchId && (
                              <div className="invalid-feedback">
                                {errors.batchId}
                              </div>
                            )}
                          </div>
                        </div>
                        {/* State */}
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label htmlFor="state" className="form-label">
                              State <span className="text-danger">*</span>
                            </label>
                            <Select
                              className={`basic-single ${
                                errors.state ? "is-invalid" : ""
                              }`}
                              classNamePrefix="select"
                              id="state"
                              name="state"
                              options={states}
                              value={getSelectedState()}
                              onChange={(selected) =>
                                handleSelectChange("state", selected)
                              }
                              placeholder="Select State"
                              isSearchable
                            />
                            {errors.state && (
                              <div className="invalid-feedback d-block">
                                {errors.state}
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Location */}
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label htmlFor="location" className="form-label">
                              Location <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control form-control-sm ${
                                errors.location ? "is-invalid" : ""
                              }`}
                              id="location"
                              name="location"
                              value={localFormData.location || ""}
                              onChange={handleChange}
                            />
                            {errors.location && (
                              <div className="invalid-feedback">
                                {errors.location}
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Job Role */}
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label htmlFor="jobRole" className="form-label">
                              Job Role <span className="text-danger">*</span>
                            </label>
                            <Select
                              className={`basic-single ${
                                errors.jobRole ? "is-invalid" : ""
                              }`}
                              classNamePrefix="select"
                              id="jobRole"
                              name="jobRole"
                              options={jobRoles}
                              value={getSelectedJobRole()}
                              onChange={(selected) =>
                                handleSelectChange("jobRole", selected)
                              }
                              placeholder="Select Job Role"
                              isSearchable
                            />
                            {errors.jobRole && (
                              <div className="invalid-feedback d-block">
                                {errors.jobRole}
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Training Partner (TP Name) */}
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label htmlFor="tpName" className="form-label">
                              Training Partner{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <Select
                              className={`basic-single ${
                                errors.tpName ? "is-invalid" : ""
                              }`}
                              classNamePrefix="select"
                              id="tpName"
                              name="tpName"
                              options={trainingPartners}
                              value={getSelectedTPName()}
                              onChange={(selected) =>
                                handleSelectChange("tpName", selected)
                              }
                              placeholder="Select Training Partner"
                              isSearchable
                            />
                            {errors.tpName && (
                              <div className="invalid-feedback d-block">
                                {errors.tpName}
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Start Date */}
                        <div className="col-md-3">
                          <div className="mb-3">
                            <label htmlFor="startDate" className="form-label">
                              Start Date <span className="text-danger">*</span>
                            </label>
                            <input
                              type="date"
                              className={`form-control form-control-sm ${
                                errors.startDate ? "is-invalid" : ""
                              }`}
                              id="startDate"
                              name="startDate"
                              value={localFormData.startDate || ""}
                              onChange={handleChange}
                            />
                            {errors.startDate && (
                              <div className="invalid-feedback">
                                {errors.startDate}
                              </div>
                            )}
                          </div>
                        </div>
                        {/* End Date */}
                        <div className="col-md-3">
                          <div className="mb-3">
                            <label htmlFor="endDate" className="form-label">
                              End Date <span className="text-danger">*</span>
                            </label>
                            <input
                              type="date"
                              className={`form-control form-control-sm ${
                                errors.endDate ? "is-invalid" : ""
                              }`}
                              id="endDate"
                              name="endDate"
                              value={localFormData.endDate || ""}
                              onChange={handleChange}
                            />
                            {errors.endDate && (
                              <div className="invalid-feedback">
                                {errors.endDate}
                              </div>
                            )}
                          </div>
                        </div>
                          {/* Add Trainer Name field here */}
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label htmlFor="trainerName" className="form-label">
                              Trainer Name <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control form-control-sm ${
                                errors.trainerName ? "is-invalid" : ""
                              }`}
                              id="trainerName"
                              name="trainerName"
                              value={localFormData.trainerName || ""}
                              onChange={handleChange}
                            />
                            {errors.trainerName && (
                              <div className="invalid-feedback">
                                {errors.trainerName}
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Status */}
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label htmlFor="status" className="form-label">
                              Status <span className="text-danger">*</span>
                            </label>
                            <Select
                              className={`basic-single ${
                                errors.status ? "is-invalid" : ""
                              }`}
                              classNamePrefix="select"
                              id="status"
                              name="status"
                              options={statusOptions}
                              value={getSelectedStatus()}
                              onChange={(selected) =>
                                handleSelectChange("status", selected)
                              }
                              placeholder="Select Status"
                              isSearchable
                            />
                            {errors.status && (
                              <div className="invalid-feedback d-block">
                                {errors.status}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Candidate Details Table */}
                      <CandidateTable
                        candidates={candidates}
                        candidateErrors={candidateErrors}
                        handleAddCandidate={handleAddCandidate}
                        handleDeleteCandidate={handleDeleteCandidate}
                        handleCandidateSelect={handleCandidateSelect}
                        loadCandidateOptions={loadCandidateOptions}
                      />

                      {/* Action Buttons */}
                      <div className="d-flex justify-content-end mt-4">
                        <button
                          type="button"
                          className="btn btn-secondary me-2"
                          onClick={handleCancel}
                        >
                          <FaTimes className="me-2" /> Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleSubmit}
                        >
                          <FaSave className="me-2" />{" "}
                          {isAddMode ? "Create Batch" : "Update Batch"}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </main>
            <ToastContainer position="top-right" autoClose={3000} />
          </div>
        </div>
      </div>
    </div>
  );
}

// import React, { useState, useEffect } from "react";
// import { useParams, useNavigate } from "react-router-dom";
// import { FaSave, FaTimes } from "react-icons/fa";
// import Select from "react-select";
// import SideNav from "../common/sidenav";
// import TopNav from "../common/topnav";
// import Loading from "../loading/loading";
// import "../common/style.css";
// import "./manageBatch.css";
// import { toast, ToastContainer } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
// import {
//   getJobrole,
//   getState,
//   getCandidateByProject,
//   createBatch,
//   selectTPName,
//   fetchBatchById,
// } from "../http/api";
// import CandidateTable from "./candidateTable";

// export default function ManageBatchForm({ mode }) {
//   const { id, projectId } = useParams();
//   const navigate = useNavigate();
//   const [loading, setLoading] = useState(false);
//   const [isSidebarOpen, setIsSidebarOpen] = useState(
//     localStorage.getItem("sb|sidebar-toggle") === "true"
//   );

//   const isAddMode = mode === "add";

//   // Form state
//   const [localFormData, setLocalFormData] = useState({
//     id: isAddMode ? null : id,
//     batchId: "",
//     state: "",
//     location: "",
//     jobRole: "",
//     tpName: "",
//     trainerName: "",
//     startDate: "",
//     endDate: "",
//     status: "Not Started",
//     projectId: projectId || null,
//   });

//   const [candidates, setCandidates] = useState([]);
//   const [errors, setErrors] = useState({});
//   const [candidateErrors, setCandidateErrors] = useState({});
//   const [selectedCandidateIds, setSelectedCandidateIds] = useState(new Set());

//   // Pagination state
//   const [currentPage, setCurrentPage] = useState(1);
//   const [rowsPerPage, setRowsPerPage] = useState(50); // default 50 per page
//   const [totalCandidates, setTotalCandidates] = useState(0);


//   // Data for dropdowns
//   const [states, setStates] = useState([]);
//   const [jobRoles, setJobRoles] = useState([]);
//   const [trainingPartners, setTrainingPartners] = useState([]);
//   const statusOptions = [
//     { value: "Not Started", label: "Not Started" },
//     { value: "Ongoing", label: "Ongoing" },
//     { value: "Cancelled", label: "Cancelled" },
//     { value: "Completed", label: "Completed" },
//   ];

//   useEffect(() => {
//     localStorage.setItem("sb|sidebar-toggle", isSidebarOpen);
//   }, [isSidebarOpen]);

//   // Load candidate options for AsyncSelect
//   const loadCandidateOptions = async (inputValue = "", page = currentPage, limit = rowsPerPage) => {
//     try {
//       if (!projectId) {
//         console.error("Project ID is missing");
//         return { data: [], total: 0 };
//       }

//       const response = await getCandidateByProject(projectId, page, limit); // send page & limit
//       const candidatesData = Array.isArray(response)
//         ? response
//         : Array.isArray(response?.data)
//           ? response.data
//           : [];

//       // filter for search
//       const filteredCandidates = candidatesData.filter((candidate) => {
//         if (!candidate || selectedCandidateIds.has(candidate.id)) return false;

//         const searchFields = [
//           candidate.candidateName?.toLowerCase() || "",
//           candidate.mobile?.toString() || "",
//           candidate.aadharNumber?.toString() || "",
//           candidate.guardianName?.toLowerCase() || "",
//         ];

//         const searchTerm = inputValue.toLowerCase();
//         return searchFields.some((field) => field.includes(searchTerm));
//       });

//       return {
//         data: filteredCandidates.map((candidate) => ({
//           value: candidate.id,
//           label: `${candidate.candidateName || "Unknown"} (${candidate.mobile || "No mobile"})`,
//           data: candidate,
//         })),
//         total: response.total || candidatesData.length, // total from backend
//       };
//     } catch (error) {
//       console.error("Error loading candidates:", error);
//       return { data: [], total: 0 };
//     }
//   };


//   // Fetch initial data based on mode
//   useEffect(() => {
//     const fetchInitialData = async () => {
//       try {
//         setLoading(true);

//         // Fetch States
//         const statesResponse = await getState();
//         const formattedStates = statesResponse.data.map((state) => ({
//           value: state.name,
//           label: state.name,
//         }));
//         setStates(formattedStates);

//         // Fetch Job Roles
//         const jobRolesResponse = await getJobrole();
//         const formattedJobRoles = jobRolesResponse.data.map((role) => ({
//           value: role.name,
//           label: role.name,
//         }));
//         setJobRoles(formattedJobRoles);

//         // Fetch Training Partners
//         const tpResponse = await selectTPName();
//         const formattedTPs = tpResponse.data.map((tp) => ({
//           value: tp.id,
//           label: `${tp.tpname} (${tp.name})`,
//         }));
//         setTrainingPartners(formattedTPs);

//         if (mode === "edit" && id) {
//           const batchResponse = await fetchBatchById(id);
//           const batchData = batchResponse.data;
//           setLocalFormData({
//             id: batchData.id,
//             batchId: batchData.batchId,
//             state: batchData.state,
//             location: batchData.location,
//             jobRole: batchData.jobRole,
//             tpName: batchData.trainingPartner?.id || "",
//             trainerName: batchData.trainerName,
//             startDate: batchData.startDate,
//             endDate: batchData.endDate,
//             status: batchData.status,
//             projectId: batchData.project?.id,
//           });
//           setCandidates(
//             (batchData.candidate || []).map((c) => ({
//               candidateName: c.candidateName,
//               mobile: c.mobile,
//               aadharNumber: c.aadharNumber,
//               guardianName: c.guardianName,
//               candidateId: c.id,
//               selectOption: {
//                 value: c.id,
//                 label: `${c.candidateName} (${c.mobile})`,
//                 data: c,
//               },
//             }))
//           );
//           setSelectedCandidateIds(
//             new Set((batchData.candidate || []).map((c) => c.id))
//           );
//         }
//       } catch (error) {
//         console.error("Error fetching initial data:", error);
//         toast.error("Failed to load initial data. Please try again.");
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchInitialData();
//   }, [mode, id, projectId]);

//   const toggleSidebar = () => {
//     setIsSidebarOpen(!isSidebarOpen);
//   };

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setLocalFormData((prevData) => ({
//       ...prevData,
//       [name]: value,
//     }));
//     if (errors[name]) {
//       setErrors((prevErrors) => ({
//         ...prevErrors,
//         [name]: null,
//       }));
//     }
//   };

//   const handleSelectChange = (name, selectedOption) => {
//     setLocalFormData((prevData) => ({
//       ...prevData,
//       [name]: selectedOption ? selectedOption.value : "",
//     }));
//     if (errors[name]) {
//       setErrors((prevErrors) => ({
//         ...prevErrors,
//         [name]: null,
//       }));
//     }
//   };

//   const handleCandidateSelect = (index, selectedOption) => {
//     const newCandidates = [...candidates];
//     const newSelectedIds = new Set(selectedCandidateIds);

//     // Remove old ID from the set if it exists
//     if (newCandidates[index].candidateId) {
//       newSelectedIds.delete(newCandidates[index].candidateId);
//     }

//     if (selectedOption) {
//       const { candidateName, mobile, aadharNumber, guardianName } =
//         selectedOption.data;
//       newCandidates[index] = {
//         ...newCandidates[index],
//         candidateName,
//         mobile,
//         aadharNumber,
//         guardianName,
//         candidateId: selectedOption.value,
//         selectOption: selectedOption,
//       };
//       newSelectedIds.add(selectedOption.value);
//     } else {
//       // Handle the case when the user clears the selection
//       newCandidates[index] = {
//         candidateName: "",
//         mobile: "",
//         aadharNumber: "",
//         guardianName: "",
//         candidateId: null,
//         selectOption: null,
//       };
//     }
//     setSelectedCandidateIds(newSelectedIds);
//     setCandidates(newCandidates);
//   };

//   const handleAddCandidate = () => {
//     setCandidates([
//       {
//         candidateName: "",
//         mobile: "",
//         aadharNumber: "",
//         guardianName: "",
//         candidateId: null,
//         selectOption: null,
//       },
//       ...candidates,
//     ]);
//   };

//   const handleDeleteCandidate = (index) => {
//     const newSelectedIds = new Set(selectedCandidateIds);
//     if (candidates[index].candidateId) {
//       newSelectedIds.delete(candidates[index].candidateId);
//     }
//     setSelectedCandidateIds(newSelectedIds);
//     setCandidates(candidates.filter((_, i) => i !== index));
//   };

//   const validateForm = () => {
//     const newErrors = {};
//     if (!localFormData.batchId.trim())
//       newErrors.batchId = "Batch ID is required.";
//     if (!localFormData.state) newErrors.state = "State is required.";
//     if (!localFormData.location.trim())
//       newErrors.location = "Location is required.";
//     if (!localFormData.jobRole) newErrors.jobRole = "Job Role is required.";
//     if (!localFormData.tpName)
//       newErrors.tpName = "Training Partner is required.";
//     if (!localFormData.trainerName.trim()) // Add this validation
//       newErrors.trainerName = "Trainer Name is required.";
//     if (!localFormData.startDate)
//       newErrors.startDate = "Start Date is required.";
//     if (!localFormData.endDate) newErrors.endDate = "End Date is required.";
//     if (!localFormData.status) newErrors.status = "Status is required.";

//     if (localFormData.startDate && localFormData.endDate) {
//       const startDate = new Date(localFormData.startDate);
//       const endDate = new Date(localFormData.endDate);
//       if (endDate < startDate) {
//         newErrors.endDate = "End date must be after start date.";
//       }
//     }
//     return newErrors;
//   };

//   const validateCandidates = () => {
//     const newCandidateErrors = {};
//     let isValid = true;

//     // if (candidates.length === 0) {
//     //   toast.error("At least one candidate is required.");
//     //   isValid = false;
//     // }

//     const candidateIds = candidates
//       .map((c) => c.candidateId)
//       .filter((id) => id !== null);

//     if (new Set(candidateIds).size !== candidateIds.length) {
//       toast.error("Duplicate candidates are not allowed.");
//       isValid = false;
//     }

//     candidates.forEach((candidate, index) => {
//       newCandidateErrors[index] = {};
//       if (!candidate.candidateId) {
//         newCandidateErrors[index].candidateId = "Please select a candidate.";
//         isValid = false;
//       }
//     });

//     setCandidateErrors(newCandidateErrors);
//     return isValid;
//   };

//   const handleSubmit = async () => {
//     const formErrors = validateForm();
//     const candidatesAreValid = validateCandidates();

//     if (Object.keys(formErrors).length > 0 || !candidatesAreValid) {
//       setErrors(formErrors);
//       if (Object.keys(formErrors).length > 0) {
//         toast.error("Please correct the form errors.");
//       }
//       return;
//     }

//     setLoading(true);

//     const dataToSubmit = {
//       batchId: localFormData.batchId,
//       state: localFormData.state,
//       location: localFormData.location,
//       jobRole: localFormData.jobRole,
//       startDate: localFormData.startDate,
//       endDate: localFormData.endDate,
//       status: localFormData.status,
//       trainerName: localFormData.trainerName,
//       project: {
//         id: projectId || localFormData.projectId,
//       },
//       trainingPartner: {
//         id: localFormData.tpName,
//       },
//       batchCandidates: candidates.map((c) => ({
//         candidate: {
//           id: c.candidateId,
//         },
//       })),
//     };

//     try {
//       if (mode === "add") {
//         await createBatch(dataToSubmit);
//         toast.success("Batch added successfully! Redirecting...");
//       } else if (mode === "edit") {
//         // const editData = {
//         //     id: localFormData.id,
//         //     projectId: localFormData.projectId,
//         //     ...dataToSubmit,
//         //   };
//         //   console.log("Updating batch with data:", JSON.stringify(editData, null, 2));
//         await createBatch({
//           id: localFormData.id,
//           projectId: localFormData.projectId,
//           ...dataToSubmit,
//         });
//         toast.success("Batch updated successfully! Redirecting...");
//       }
//       setTimeout(() => navigate("/managebatch"), 1500);
//     } catch (error) {
//       console.error(`Error ${isAddMode ? "adding" : "updating"} batch:`, error);
//       toast.error(
//         `Error ${isAddMode ? "adding" : "updating"} batch. Please try again.`
//       );
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleCancel = () => {
//     toast.info("Action cancelled.");
//     navigate(-1);
//   };

//   // Get the current selected values for the selects
//   const getSelectedState = () => {
//     return states.find((state) => state.value === localFormData.state) || null;
//   };

//   const getSelectedJobRole = () => {
//     return (
//       jobRoles.find((role) => role.value === localFormData.jobRole) || null
//     );
//   };

//   const getSelectedTPName = () => {
//     return (
//       trainingPartners.find((tp) => tp.value === localFormData.tpName) || null
//     );
//   };

//   const getSelectedStatus = () => {
//     return (
//       statusOptions.find((status) => status.value === localFormData.status) ||
//       null
//     );
//   };

//   return (
//     <div className="ManageBatchForm">
//       {loading && <Loading />}
//       <div
//         className={`sb-nav-fixed ${isSidebarOpen ? "sb-sidenav-toggled" : ""}`}
//       >
//         <TopNav toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} />
//         <div id="layoutSidenav">
//           <SideNav isSidebarOpen={isSidebarOpen} />
//           <div id="layoutSidenav_content">
//             <main>
//               <div className="container-fluid px-4">
//                 <h4 className="mt-4">
//                   {isAddMode
//                     ? "Add New Batch"
//                     : `Edit Batch - ${localFormData.batchId}`}
//                 </h4>
//                 <div className="card mb-4">
//                   <div className="card-header">
//                     <i className="fas fa-table me-1"></i>
//                     Batch Details Form
//                   </div>
//                   <div className="card-body shadow-sm border-0">
//                     <form className="data-table" style={{ padding: "20px" }}>
//                       <div className="row">
//                         {/* Batch ID */}
//                         <div className="col-md-6">
//                           <div className="mb-3">
//                             <label htmlFor="batchId" className="form-label">
//                               Batch ID <span className="text-danger">*</span>
//                             </label>
//                             <input
//                               type="text"
//                               className={`form-control form-control-sm ${errors.batchId ? "is-invalid" : ""
//                                 }`}
//                               id="batchId"
//                               name="batchId"
//                               value={localFormData.batchId || ""}
//                               onChange={handleChange}
//                               disabled={!isAddMode}
//                             />
//                             {errors.batchId && (
//                               <div className="invalid-feedback">
//                                 {errors.batchId}
//                               </div>
//                             )}
//                           </div>
//                         </div>
//                         {/* State */}
//                         <div className="col-md-6">
//                           <div className="mb-3">
//                             <label htmlFor="state" className="form-label">
//                               State <span className="text-danger">*</span>
//                             </label>
//                             <Select
//                               className={`basic-single ${errors.state ? "is-invalid" : ""
//                                 }`}
//                               classNamePrefix="select"
//                               id="state"
//                               name="state"
//                               options={states}
//                               value={getSelectedState()}
//                               onChange={(selected) =>
//                                 handleSelectChange("state", selected)
//                               }
//                               placeholder="Select State"
//                               isSearchable
//                             />
//                             {errors.state && (
//                               <div className="invalid-feedback d-block">
//                                 {errors.state}
//                               </div>
//                             )}
//                           </div>
//                         </div>
//                         {/* Location */}
//                         <div className="col-md-6">
//                           <div className="mb-3">
//                             <label htmlFor="location" className="form-label">
//                               Location <span className="text-danger">*</span>
//                             </label>
//                             <input
//                               type="text"
//                               className={`form-control form-control-sm ${errors.location ? "is-invalid" : ""
//                                 }`}
//                               id="location"
//                               name="location"
//                               value={localFormData.location || ""}
//                               onChange={handleChange}
//                             />
//                             {errors.location && (
//                               <div className="invalid-feedback">
//                                 {errors.location}
//                               </div>
//                             )}
//                           </div>
//                         </div>
//                         {/* Job Role */}
//                         <div className="col-md-6">
//                           <div className="mb-3">
//                             <label htmlFor="jobRole" className="form-label">
//                               Job Role <span className="text-danger">*</span>
//                             </label>
//                             <Select
//                               className={`basic-single ${errors.jobRole ? "is-invalid" : ""
//                                 }`}
//                               classNamePrefix="select"
//                               id="jobRole"
//                               name="jobRole"
//                               options={jobRoles}
//                               value={getSelectedJobRole()}
//                               onChange={(selected) =>
//                                 handleSelectChange("jobRole", selected)
//                               }
//                               placeholder="Select Job Role"
//                               isSearchable
//                             />
//                             {errors.jobRole && (
//                               <div className="invalid-feedback d-block">
//                                 {errors.jobRole}
//                               </div>
//                             )}
//                           </div>
//                         </div>
//                         {/* Training Partner (TP Name) */}
//                         <div className="col-md-6">
//                           <div className="mb-3">
//                             <label htmlFor="tpName" className="form-label">
//                               Training Partner{" "}
//                               <span className="text-danger">*</span>
//                             </label>
//                             <Select
//                               className={`basic-single ${errors.tpName ? "is-invalid" : ""
//                                 }`}
//                               classNamePrefix="select"
//                               id="tpName"
//                               name="tpName"
//                               options={trainingPartners}
//                               value={getSelectedTPName()}
//                               onChange={(selected) =>
//                                 handleSelectChange("tpName", selected)
//                               }
//                               placeholder="Select Training Partner"
//                               isSearchable
//                             />
//                             {errors.tpName && (
//                               <div className="invalid-feedback d-block">
//                                 {errors.tpName}
//                               </div>
//                             )}
//                           </div>
//                         </div>
//                         {/* Start Date */}
//                         <div className="col-md-3">
//                           <div className="mb-3">
//                             <label htmlFor="startDate" className="form-label">
//                               Start Date <span className="text-danger">*</span>
//                             </label>
//                             <input
//                               type="date"
//                               className={`form-control form-control-sm ${errors.startDate ? "is-invalid" : ""
//                                 }`}
//                               id="startDate"
//                               name="startDate"
//                               value={localFormData.startDate || ""}
//                               onChange={handleChange}
//                             />
//                             {errors.startDate && (
//                               <div className="invalid-feedback">
//                                 {errors.startDate}
//                               </div>
//                             )}
//                           </div>
//                         </div>
//                         {/* End Date */}
//                         <div className="col-md-3">
//                           <div className="mb-3">
//                             <label htmlFor="endDate" className="form-label">
//                               End Date <span className="text-danger">*</span>
//                             </label>
//                             <input
//                               type="date"
//                               className={`form-control form-control-sm ${errors.endDate ? "is-invalid" : ""
//                                 }`}
//                               id="endDate"
//                               name="endDate"
//                               value={localFormData.endDate || ""}
//                               onChange={handleChange}
//                             />
//                             {errors.endDate && (
//                               <div className="invalid-feedback">
//                                 {errors.endDate}
//                               </div>
//                             )}
//                           </div>
//                         </div>
//                         {/* Add Trainer Name field here */}
//                         <div className="col-md-6">
//                           <div className="mb-3">
//                             <label htmlFor="trainerName" className="form-label">
//                               Trainer Name <span className="text-danger">*</span>
//                             </label>
//                             <input
//                               type="text"
//                               className={`form-control form-control-sm ${errors.trainerName ? "is-invalid" : ""
//                                 }`}
//                               id="trainerName"
//                               name="trainerName"
//                               value={localFormData.trainerName || ""}
//                               onChange={handleChange}
//                             />
//                             {errors.trainerName && (
//                               <div className="invalid-feedback">
//                                 {errors.trainerName}
//                               </div>
//                             )}
//                           </div>
//                         </div>
//                         {/* Status */}
//                         <div className="col-md-6">
//                           <div className="mb-3">
//                             <label htmlFor="status" className="form-label">
//                               Status <span className="text-danger">*</span>
//                             </label>
//                             <Select
//                               className={`basic-single ${errors.status ? "is-invalid" : ""
//                                 }`}
//                               classNamePrefix="select"
//                               id="status"
//                               name="status"
//                               options={statusOptions}
//                               value={getSelectedStatus()}
//                               onChange={(selected) =>
//                                 handleSelectChange("status", selected)
//                               }
//                               placeholder="Select Status"
//                               isSearchable
//                             />
//                             {errors.status && (
//                               <div className="invalid-feedback d-block">
//                                 {errors.status}
//                               </div>
//                             )}
//                           </div>
//                         </div>
//                       </div>

//                       {/* Candidate Details Table */}
//                       <CandidateTable
//                         candidates={candidates}
//                         candidateErrors={candidateErrors}
//                         handleAddCandidate={handleAddCandidate}
//                         handleDeleteCandidate={handleDeleteCandidate}
//                         handleCandidateSelect={handleCandidateSelect}
//                         loadCandidateOptions={loadCandidateOptions}


//                       />

//                       {/* Candidate Details Table */}
//                       {/* <CandidateTable
//                         candidates={candidates}
//                         candidateErrors={candidateErrors}
//                         handleAddCandidate={handleAddCandidate}
//                         handleDeleteCandidate={handleDeleteCandidate}
//                         handleCandidateSelect={handleCandidateSelect}
//                         loadCandidateOptions={loadCandidateOptions}
//                       /> */}

//                       {/* Pagination Controls - Step 3 */}
//                       {/* <div className="d-flex justify-content-between align-items-center mt-3">
//                         <div>
//                           Showing {(currentPage - 1) * rowsPerPage + 1} –{" "}
//                           {Math.min(currentPage * rowsPerPage, totalCandidates)} of {totalCandidates}
//                         </div>

//                         <div className="d-flex align-items-center gap-2">
//                           <select
//                             className="form-select form-select-sm"
//                             value={rowsPerPage}
//                             onChange={(e) => {
//                               setRowsPerPage(Number(e.target.value));
//                               setCurrentPage(1); // reset page
//                             }}
//                           >
//                             {[10, 25, 50, 100].map((n) => (
//                               <option key={n} value={n}>
//                                 {n} per page
//                               </option>
//                             ))}
//                           </select>

//                           <button
//                             className="btn btn-sm btn-outline-primary"
//                             disabled={currentPage === 1}
//                             onClick={() => setCurrentPage((p) => p - 1)}
//                           >
//                             Prev
//                           </button>
//                           <span>
//                             Page {currentPage} of {Math.ceil(totalCandidates / rowsPerPage)}
//                           </span>
//                           <button
//                             className="btn btn-sm btn-outline-primary"
//                             disabled={currentPage >= Math.ceil(totalCandidates / rowsPerPage)}
//                             onClick={() => setCurrentPage((p) => p + 1)}
//                           >
//                             Next
//                           </button>
//                         </div>
//                       </div> */}


//                       {/* Action Buttons */}
//                       <div className="d-flex justify-content-end mt-4">
//                         <button
//                           type="button"
//                           className="btn btn-secondary me-2"
//                           onClick={handleCancel}
//                         >
//                           <FaTimes className="me-2" /> Cancel
//                         </button>
//                         <button
//                           type="button"
//                           className="btn btn-primary"
//                           onClick={handleSubmit}
//                         >
//                           <FaSave className="me-2" />{" "}
//                           {isAddMode ? "Create Batch" : "Update Batch"}
//                         </button>
//                       </div>
//                     </form>
//                   </div>
//                 </div>
//               </div>
//             </main>
//             <ToastContainer position="top-right" autoClose={3000} />
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }